import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React, {useEffect} from 'react';

import {overridePurpose} from 'archive/ArchiveActions';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import * as Context from 'Context';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {StudySchemasPageQuery} from 'studies/study-schemas/__generated/StudySchemasPageQuery';
import {getCreateStudySchemaURL, getStudySchemaURL} from 'studies/study-schemas/StudySchemaDAO';
import {withLoadingScreen} from 'util/Promises';

import {bulkArchive, bulkUnarchive} from './StudySchemaDAO';

type StudySchema = StudySchemasPageQuery['studySchemas'][number];

interface StudySchemasPageProps {
  schemas: Array<StudySchema> | null;
  errorMessage?: string;
  areOrganizationSchemasCreatable: boolean;
  refetch: () => void;
}

export const StudySchemasPage = (props: StudySchemasPageProps): JSX.Element => {
  const bulkArchiveStudySchemas = async (ids: Array<string>, purpose: string): Promise<void> => {
    try {
      await withLoadingScreen(
        (async () => {
          await Context.usingAsync(
            {analyticsMetadata: {actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE}},
            null,
            async () => {
              await bulkArchive(ids, overridePurpose(purpose, ids));
              notifications.success('Study schema successfully archived.');
            }
          );
        })(),
        'Archiving...'
      );
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.notifyError(error);
    }
    props.refetch();
  };

  const bulkUnarchiveStudySchemas = async (ids: Array<string>): Promise<void> => {
    try {
      await Context.usingAsync(
        {analyticsMetadata: {actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE}},
        null,
        async () => {
          await bulkUnarchive(ids);
          notifications.success('Study schema successfully unarchived.');
        }
      );
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.notifyError(error);
    }
    props.refetch();
  };

  useEffect(() => {
    benchlingUtil.setWindowTitle('Study Schemas');
  });

  return (
    <SchemasListing
      schemas={props.schemas}
      errorMessage={props.errorMessage}
      schemaType={SCHEMA_TYPES.STUDY}
      navigateToCreate={() => {
        benchlingUtil.navigate(null, getCreateStudySchemaURL());
      }}
      urlForSchema={getStudySchemaURL}
      archiveSchemas={bulkArchiveStudySchemas}
      unarchiveSchemas={bulkUnarchiveStudySchemas}
      isWritable={props.areOrganizationSchemasCreatable}
    />
  );
};

export const STUDY_SCHEMAS_PAGE_QUERY: DocumentNode = gql`
  query StudySchemasPageQuery @skipAudit @engComponent(name: STUDIES) {
    studySchemas {
      id
      name
      archiveRecord {
        purpose
      }
    }
  }
`;

export default function ConnectedStudySchemasPage(): JSX.Element {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;
  const queryResult = useBenchlingQuery<StudySchemasPageQuery>(STUDY_SCHEMAS_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const studySchemas =
    queryResult.type === QueryResultType.SUCCESS
      ? naturalSortByKey(queryResult.data.studySchemas, 'name')
      : null;

  return (
    <StudySchemasPage
      schemas={studySchemas}
      errorMessage={queryResult.type === QueryResultType.ERROR ? 'Error loading study schemas' : undefined}
      areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
      refetch={queryResult.refetch}
    />
  );
}
